export type ClientRuntimeCode = 'abort' | 'canvas_context_lost' | 'memory_pressure' | 'range_error' | 'script_error' | 'type_error' | 'unknown_error';

export interface ClientRuntimeDiagnostic {
  eventType: 'window_error' | 'unhandled_rejection';
  route: string;
  errorName: string;
  code: ClientRuntimeCode;
  clientRuntime: 'android_webview' | 'android_browser' | 'ios_webview' | 'browser';
  clientVersion?: string;
}

const safeToken = (value: string, fallback: string, maximum = 64): string => {
  const normalized = value.replace(/[^A-Za-z0-9_.-]/g, '_').slice(0, maximum);
  return normalized || fallback;
};

export function clientRuntime(userAgent: string): Pick<ClientRuntimeDiagnostic, 'clientRuntime' | 'clientVersion'> {
  const chromeVersion = userAgent.match(/(?:Chrome|CriOS)\/([0-9.]+)/i)?.[1];
  const webkitVersion = userAgent.match(/Version\/([0-9.]+)/i)?.[1];
  if (/Android/i.test(userAgent) && /(?:;\s*wv\)|\bwv\b)/i.test(userAgent)) return { clientRuntime: 'android_webview', ...(chromeVersion ? { clientVersion: chromeVersion.slice(0, 32) } : {}) };
  if (/Android/i.test(userAgent)) return { clientRuntime: 'android_browser', ...(chromeVersion ? { clientVersion: chromeVersion.slice(0, 32) } : {}) };
  if (/(?:iPhone|iPad|iPod)/i.test(userAgent) && !/(?:CriOS|FxiOS)/i.test(userAgent)) return { clientRuntime: 'ios_webview', ...(webkitVersion ? { clientVersion: webkitVersion.slice(0, 32) } : {}) };
  return { clientRuntime: 'browser', ...((chromeVersion || webkitVersion) ? { clientVersion: (chromeVersion || webkitVersion)!.slice(0, 32) } : {}) };
}

function errorDetails(value: unknown, eventType: ClientRuntimeDiagnostic['eventType']): Pick<ClientRuntimeDiagnostic, 'errorName' | 'code'> {
  const name = value instanceof Error ? safeToken(value.name, 'Error') : eventType === 'window_error' ? 'ErrorEvent' : 'NonErrorRejection';
  const message = value instanceof Error ? value.message.toLowerCase() : '';
  if (/out of memory|memory pressure|allocation failed|context lost/.test(message)) return { errorName: name, code: message.includes('context lost') ? 'canvas_context_lost' : 'memory_pressure' };
  if (name === 'AbortError') return { errorName: name, code: 'abort' };
  if (name === 'TypeError') return { errorName: name, code: 'type_error' };
  if (name === 'RangeError') return { errorName: name, code: 'range_error' };
  if (eventType === 'window_error') return { errorName: name, code: 'script_error' };
  return { errorName: name, code: 'unknown_error' };
}

export function installRuntimeDiagnostics(options: {
  route: () => string;
  report: (diagnostic: ClientRuntimeDiagnostic) => void;
  userAgent?: string;
  maximumReports?: number;
}): () => void {
  const runtime = clientRuntime(options.userAgent ?? navigator.userAgent);
  let reports = 0;
  const emit = (eventType: ClientRuntimeDiagnostic['eventType'], value: unknown) => {
    if (reports >= (options.maximumReports ?? 5)) return;
    reports += 1;
    options.report({ eventType, route: safeToken(options.route(), 'unknown_route'), ...errorDetails(value, eventType), ...runtime });
  };
  const onError = (event: ErrorEvent) => emit('window_error', event.error);
  const onUnhandledRejection = (event: PromiseRejectionEvent) => emit('unhandled_rejection', event.reason);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
  };
}
